import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { Bolsa, LineaDeBolsa } from '../../../types';
import { StatusBadge } from '../../CentroCotizaciones/components/StatusBadge';
import { EstadoBolsa } from '../../CentroCotizaciones/state/bolsaMachine';
import { productosDistintos } from '../utils/productosDistintos';

// SPEC-119 — tokens del paquete de diseño fuente (ADR-002), aplicados a los componentes nuevos.
const PURPLE = '#93408F';
const PURPLE_SOFT = '#B06CAD';
const INK = '#2E2430';
const MUTED = '#A79AA7';
const FAINT = '#C9BFC9';
const HAIR = '#F0E9F0';
const WASH = '#FAF7FA';
const RED = '#CB4444';

interface BagStripProps {
  bolsas: Bolsa[];
  bolsaActivaId: string;
  onSeleccionarBolsa: (bolsaId: string) => void;
  onNuevaBolsa: () => void;
  onGestionar: () => void;
  onBorrarBolsa: (bolsaId: string) => void;
  onPreviewPdf: (bolsaId: string) => void;
  onCrearPdf: () => void;
  crearPdfDisabled: boolean;
  onBorrarBolsaActiva: () => void;
  borrarBolsaActivaDisabled: boolean;
}

function formatCompacto(valor: number): string {
  if (valor >= 1_000_000) return `$ ${(valor / 1_000_000).toFixed(1).replace('.', ',')} M`;
  return `$ ${valor.toLocaleString('es-CO')}`;
}

// RN-003 (SPEC-119): fotos reales de producto (imagen_url), fallback de inicial si falla/no hay.
function MiniFoto({ linea, active }: { linea: LineaDeBolsa; active: boolean }) {
  const [fallo, setFallo] = React.useState(false);
  const mostrarFallback = fallo || !linea.imagen_url;
  return (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: '6px',
        border: `1px solid ${HAIR}`,
        background: active ? WASH : '#fff',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {mostrarFallback ? (
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: MUTED }}>
          {(linea.nombre_producto ?? '?').charAt(0).toUpperCase()}
        </Typography>
      ) : (
        <Box
          component="img"
          src={linea.imagen_url ?? undefined}
          alt=""
          loading="lazy"
          data-testid="chip-mini-foto"
          onError={() => setFallo(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
    </Box>
  );
}

function BagChip({
  bolsa,
  activa,
  onClick,
  onBorrar,
  onPreview,
}: {
  bolsa: Bolsa;
  activa: boolean;
  onClick: () => void;
  onBorrar: () => void;
  onPreview: () => void;
}) {
  // SPEC-133 (RN-002): codigo_cotizacion es la señal confiable de "esta bolsa tiene PDF" —
  // se asigna atómicamente junto al estado en la misma operación de convertir (SPEC-126 RN-001).
  const tienePdf = bolsa.codigo_cotizacion != null;
  // SPEC-141 (RN-004, CA-007): la franja ahora lista todos los estados (no solo abiertas),
  // así que cada chip expone su estado con el mismo tag legible por-estado de SPEC-132/097.
  const estadoBolsa = bolsa.estado_codigo as EstadoBolsa | undefined;
  const lineas = bolsa.lineas ?? [];
  // SPEC-168 (RN-002, RN-004): fotos por producto distinto (una miniatura por producto_id, la de su
  // primera línea), no una por línea. El badge "+N" cuenta productos distintos restantes (RN-003).
  const distintos = productosDistintos(lineas);
  const primeras4 = distintos.slice(0, 4);
  const restantes = distintos.length - primeras4.length;
  // SPEC-168 (RN-005): "unds" no cambia — sigue sumando la cantidad de TODAS las líneas.
  const unidades = lineas.reduce((acc, l) => acc + l.cantidad, 0);
  // SPEC-168 (RN-001, RN-007): "referencias" = productos distintos cuando el chip trae sus líneas;
  // si no las trae (endpoint de lista sin líneas), degrada a lineas_count como hoy.
  const refs = lineas.length > 0 ? distintos.length : bolsa.lineas_count;

  return (
    <Box
      data-testid={`bag-chip-${bolsa.id}`}
      onClick={onClick}
      sx={{
        position: 'relative',
        minWidth: 186,
        padding: '10px 30px 9px 14px',
        borderRadius: '14px',
        border: activa ? `1.5px solid ${PURPLE}` : `1.5px solid ${HAIR}`,
        background: activa ? '#fff' : WASH,
        boxShadow: activa
          ? '0 2px 6px rgba(147,64,143,.10), 0 8px 20px rgba(46,36,48,.08)'
          : 'none',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'box-shadow .16s, background .16s',
        '&:hover': { background: '#fff', boxShadow: '0 5px 14px rgba(46,36,48,.08)' },
        '&:hover .bag-chip-trash': { opacity: 1 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {activa && <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: PURPLE }} />}
        {/* SPEC-182 (RN-006/CA-007/CA-008): con codigo_cotizacion, el chip pasa de identidad
            de bolsa a identidad de cotización — mismo elemento, solo cambia el texto. */}
        <Typography data-testid={`bag-chip-identificador-${bolsa.id}`} sx={{ fontSize: 15, fontWeight: 700, color: INK }}>
          {bolsa.codigo_cotizacion ?? bolsa.codigo_bolsa ?? bolsa.nombre}
        </Typography>
      </Box>
      {/* SPEC-141 (RN-004, CA-007): indicador de estado por chip. */}
      {estadoBolsa && (
        <Box data-testid={`bag-chip-estado-${bolsa.id}`} sx={{ mt: '3px' }}>
          <StatusBadge estado={estadoBolsa} />
        </Box>
      )}
      <Typography
        sx={{
          fontSize: 13,
          color: MUTED,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          mt: '2px',
        }}
      >
        {(bolsa.cliente_razon_social ?? 'Sin cliente asociado')} · {refs} ref{refs === 1 ? '' : 's'} · {formatCompacto(bolsa.total)} · {unidades.toLocaleString('es-CO')} unds
      </Typography>
      <Box sx={{ display: 'flex', gap: '4px', mt: '8px' }}>
        {primeras4.map(l => <MiniFoto key={l.id} linea={l} active={activa} />)}
        {restantes > 0 && (
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: MUTED, alignSelf: 'center' }}>
            +{restantes}
          </Typography>
        )}
      </Box>
      <Box sx={{ position: 'absolute', top: 7, right: 7, display: 'flex', gap: '2px' }}>
        {/* SPEC-133 (CA-004, CA-005, RN-002): visible solo si ya existe un PDF generado. */}
        {tienePdf && (
          <Tooltip title="Ver PDF de la cotización">
            <IconButton
              data-testid={`bag-chip-preview-pdf-${bolsa.id}`}
              size="small"
              onClick={(e) => { e.stopPropagation(); onPreview(); }}
              sx={{
                width: 24,
                height: 24,
                color: FAINT,
                opacity: 0.85,
                transition: 'opacity .15s, background .15s, color .15s, transform .15s',
                '&:hover': {
                  opacity: 1,
                  color: PURPLE,
                  background: 'rgba(147,64,143,.12)',
                  transform: 'scale(1.1)',
                },
              }}
            >
              <PictureAsPdfOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={`Borrar bolsa ${bolsa.codigo_bolsa ?? bolsa.nombre}`}>
          <IconButton
            className="bag-chip-trash"
            data-testid={`bag-chip-trash-${bolsa.id}`}
            size="small"
            onClick={(e) => { e.stopPropagation(); onBorrar(); }}
            sx={{
              width: 24,
              height: 24,
              color: FAINT,
              opacity: 0.85,
              transition: 'opacity .15s, background .15s, color .15s, transform .15s',
              '&:hover': {
                opacity: 1,
                color: RED,
                background: 'rgba(203,68,68,.12)',
                transform: 'scale(1.1)',
              },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

// SPEC-119 (R-02..R-05): franja de chips de bolsas abiertas, reemplaza el selector dropdown.
// SPEC-136 (RN-004, RN-005): "Crear PDF" y "Borrar bolsa" (bolsa activa) se reubican aquí,
// junto a "Gestionar bolsas", en estilo sutil (texto + ícono, sin fondo).
export function BagStrip({
  bolsas,
  bolsaActivaId,
  onSeleccionarBolsa,
  onNuevaBolsa,
  onGestionar,
  onBorrarBolsa,
  onPreviewPdf,
  onCrearPdf,
  crearPdfDisabled,
  onBorrarBolsaActiva,
  borrarBolsaActivaDisabled,
}: BagStripProps) {
  return (
    <Box
      data-testid="bag-strip"
      sx={{
        background: '#fff',
        border: `1px solid ${HAIR}`,
        borderRadius: '20px',
        padding: '16px 20px 14px',
        mb: 2,
        boxShadow: '0 1px 2px rgba(46,36,48,.03), 0 6px 16px rgba(46,36,48,.05)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '12px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Box sx={{ width: 18, height: 3, borderRadius: '999px', background: PURPLE }} />
          {/* SPEC-205 (RN-008): N cuenta solo los chips en BORRADOR; la bolsa actual antepuesta no suma. */}
          <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: INK }}>
            COTIZACIONES EN BORRADOR · {bolsas.filter(b => b.estado_codigo === EstadoBolsa.BORRADOR).length}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Box
            component="button"
            data-testid="btn-crear-pdf-header"
            onClick={onCrearPdf}
            disabled={crearPdfDisabled}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              border: `1px solid ${HAIR}`,
              borderRadius: '999px',
              background: 'transparent',
              padding: '8px 16px',
              fontSize: 15,
              fontWeight: 600,
              color: PURPLE,
              cursor: 'pointer',
              '&:hover': { borderColor: PURPLE_SOFT, background: 'rgba(147,64,143,.06)' },
              '&:disabled': { opacity: 0.4, cursor: 'not-allowed' },
            }}
          >
            <PictureAsPdfOutlinedIcon sx={{ fontSize: 18 }} /> Crear PDF
          </Box>
          <Box
            component="button"
            data-testid="btn-borrar-bolsa-activa"
            onClick={onBorrarBolsaActiva}
            disabled={borrarBolsaActivaDisabled}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              border: `1px solid ${HAIR}`,
              borderRadius: '999px',
              background: 'transparent',
              padding: '8px 16px',
              fontSize: 15,
              fontWeight: 600,
              color: '#B06868',
              cursor: 'pointer',
              '&:hover': { borderColor: RED, color: RED, background: 'rgba(203,68,68,.06)' },
              '&:disabled': { opacity: 0.4, cursor: 'not-allowed' },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 18 }} /> Borrar bolsa
          </Box>
          <Box
            component="button"
            data-testid="btn-gestionar-bolsas"
            onClick={onGestionar}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              border: `1px solid ${HAIR}`,
              borderRadius: '999px',
              background: 'transparent',
              padding: '8px 16px',
              fontSize: 15,
              fontWeight: 600,
              color: INK,
              cursor: 'pointer',
              '&:hover': { borderColor: PURPLE_SOFT, color: PURPLE },
            }}
          >
            <SettingsOutlinedIcon sx={{ fontSize: 18 }} /> Gestionar bolsas
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: '10px', overflowX: 'auto', pb: '4px' }}>
        {bolsas.map(bolsa => (
          <BagChip
            key={bolsa.id}
            bolsa={bolsa}
            activa={bolsa.id === bolsaActivaId}
            onClick={() => onSeleccionarBolsa(bolsa.id)}
            onBorrar={() => onBorrarBolsa(bolsa.id)}
            onPreview={() => onPreviewPdf(bolsa.id)}
          />
        ))}
        <Box
          data-testid="bag-chip-nueva"
          onClick={onNuevaBolsa}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            minWidth: 100,
            minHeight: 64,
            border: `1.5px dashed ${PURPLE_SOFT}`,
            borderRadius: '14px',
            color: PURPLE,
            fontSize: 15,
            fontWeight: 700,
            cursor: 'pointer',
            flexShrink: 0,
            '&:hover': { background: 'rgba(147,64,143,.06)' },
          }}
        >
          <AddIcon sx={{ fontSize: 20 }} /> Nueva
        </Box>
      </Box>
    </Box>
  );
}
